import { useState } from 'react';
import { adminApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, Upload, ImageIcon } from 'lucide-react';

export function SettingsContentTab({ settings, update }) {
  const [uploadingLogo, setUploadingLogo] = useState(false);

  return (
    <div className="space-y-6">
      {/* Logo du restaurant */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-4">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Logo du restaurant</h3>
        <p className="text-xs text-charcoal/50">Uploadez le logo de votre restaurant. Il sera affiché dans la barre de navigation et le pied de page du site. Si aucun logo n'est défini, le nom du restaurant sera affiché en texte.</p>
        {settings.restaurant_logo_url ? (
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl border border-garden-200 bg-garden-50 flex items-center justify-center overflow-hidden">
              <img
                src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
                alt="Logo"
                className="max-w-full max-h-full object-contain"
                data-testid="admin-logo-preview"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="logo-upload-replace"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingLogo(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await adminApi.uploadLogo(fd);
                      update('restaurant_logo_url', res.data.url);
                      toast.success('Logo mis à jour');
                    } catch { toast.error('Erreur lors de l\'upload'); }
                    setUploadingLogo(false);
                  }}
                />
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-garden-100 text-garden-800 text-sm rounded-lg hover:bg-garden-200 transition-colors">
                  <Upload size={14} /> Remplacer
                </span>
              </label>
              <button
                onClick={async () => {
                  try {
                    await adminApi.deleteLogo();
                    update('restaurant_logo_url', '');
                    toast.success('Logo supprimé');
                  } catch { toast.error('Erreur'); }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 text-sm rounded-lg hover:bg-red-50 transition-colors"
                data-testid="logo-delete-btn"
              >
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
          </div>
        ) : (
          <label className="cursor-pointer flex flex-col items-center justify-center gap-2 border-2 border-dashed border-garden-300 rounded-xl py-8 hover:border-garden-500 hover:bg-garden-50/50 transition-colors" data-testid="logo-upload-zone">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="logo-upload-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadingLogo(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  const res = await adminApi.uploadLogo(fd);
                  update('restaurant_logo_url', res.data.url);
                  toast.success('Logo uploadé');
                } catch { toast.error('Erreur lors de l\'upload'); }
                setUploadingLogo(false);
              }}
            />
            {uploadingLogo ? (
              <div className="w-8 h-8 border-2 border-garden-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <ImageIcon size={32} className="text-garden-400" />
                <span className="text-sm text-garden-600 font-medium">Cliquez pour uploader un logo</span>
                <span className="text-xs text-charcoal/40">PNG, JPG, SVG — 500x500px recommandé</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section Hero (accueil)</h3>
        <div>
          <Label className="text-sm">Titre principal</Label>
          <Input value={settings.hero_title || ''} onChange={e => update('hero_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Sous-titre</Label>
          <Textarea value={settings.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)} className="border-garden-200 rounded-xl" rows={2} />
        </div>
      </div>

      {/* À propos */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section "Notre cuisine"</h3>
        <div>
          <Label className="text-sm">Titre</Label>
          <Input value={settings.about_title || ''} onChange={e => update('about_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Texte (utilisez \n pour séparer les paragraphes)</Label>
          <Textarea value={settings.about_text || ''} onChange={e => update('about_text', e.target.value)} className="border-garden-200 rounded-xl" rows={4} />
        </div>
        <div>
          <Label className="text-sm">Tags (badges)</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {(settings.about_tags || []).map((tag) => (
              <span key={tag} className="bg-garden-100 text-garden-800 px-3 py-1 rounded-full text-sm flex items-center gap-1.5">
                {tag}
                <button onClick={() => update('about_tags', (settings.about_tags || []).filter(t => t !== tag))} className="text-garden-600 hover:text-red-500"><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Atmosphère */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section "Atmosphère / Terrasse"</h3>
        <div>
          <Label className="text-sm">Titre</Label>
          <Input value={settings.atmosphere_title || ''} onChange={e => update('atmosphere_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Texte</Label>
          <Textarea value={settings.atmosphere_text || ''} onChange={e => update('atmosphere_text', e.target.value)} className="border-garden-200 rounded-xl" rows={4} />
        </div>
      </div>

      {/* Boutons */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Textes des boutons</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Bouton Réserver</Label>
            <Input value={settings.btn_reserve || ''} onChange={e => update('btn_reserve', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Réserver une table" />
          </div>
          <div>
            <Label className="text-sm">Bouton Carte</Label>
            <Input value={settings.btn_menu || ''} onChange={e => update('btn_menu', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Voir la carte" />
          </div>
          <div>
            <Label className="text-sm">Bouton Commander</Label>
            <Input value={settings.btn_order || ''} onChange={e => update('btn_order', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Commander" />
          </div>
          <div>
            <Label className="text-sm">Bouton Appeler</Label>
            <Input value={settings.btn_call || ''} onChange={e => update('btn_call', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Appeler" />
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section finale (CTA)</h3>
        <div>
          <Label className="text-sm">Titre</Label>
          <Input value={settings.cta_title || ''} onChange={e => update('cta_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Texte</Label>
          <Textarea value={settings.cta_text || ''} onChange={e => update('cta_text', e.target.value)} className="border-garden-200 rounded-xl" rows={2} />
        </div>
      </div>
    </div>
  );
}
